import React, { useEffect, useState } from 'react';
import { useDocumentTitle } from '../../hooks/use-document-title';
import {
  ContentAndButtonsContainer,
  NavbarAndContentContainer,
} from '../../layout/layout-container.styled';
import { DefaultComponentProps } from '../../app-router/component-props';
import { Staleable } from '../../../motion';
import { Text, VisuallyHidden, font } from '../../../style-utils/text';
import {
  ReportToggleButton,
  ButtonsWrapper,
  ContentContainer,
  EmptyState,
  DeskDayNote,
  ReportSwitch,
} from './view-daily-report.styled';
import { BackBar, BackButton } from '../../layout/back-button';
import { ReportsTable } from './report-table';
import { fetchAllItems } from '../../requests/requests';
import { ReportSkeleton } from '../../skeleton/report-skeleton';
import {
  addDays,
  deskDayOf,
  formatDayKey,
} from '../../../function-utils/building-time';
import { useClock } from '../../../context/clock-context';
import { ClockNote } from '../../clock-note/clock-note';

// The rail, the building panel and <main> come from DeskShell now
// (layout/desk-shell.tsx); this screen renders only what goes inside the card.
export const ViewDailyReport: React.FC<DefaultComponentProps> = ({
  selectedBuilding,
}) => {
  useDocumentTitle('Daily report');

  const [isVisitorLog, setIsVisitorLog] = useState(false);

  // Starts TRUE when there is a building to read: the effect below fires on
  // the first commit, and starting false painted an empty table for one frame
  // before the skeleton replaced it.
  const [loading, setLoading] = useState(() => Boolean(selectedBuilding));
  const [error, setError] = useState('');
  // eslint-disable-next-line
  const [logs, setLogs] = useState<any>();

  /* THE BUILDING'S CLOCK — this is a desk screen (R-T12): its day, its row
     times and its label are the building's, whatever the device says and
     whoever is standing at the desk. */
  const buildingZone = selectedBuilding?.timezone || '';
  const clock = useClock({
    buildingId: selectedBuilding?.id,
    buildingZone,
    // An owner at the desk is answered on their own clock by the shared
    // endpoint; the surface still decides. The response only matters for a
    // building with no zone of its own, to say so.
    response: logs,
  });

  /* THE DESK DAY THAT IS OPEN IN THE BUILDING, as a day key: the building's
     calendar date, or the day before when its wall clock has not reached 07:00.
     The server reads this key on the building's clock and cuts 07:00 → 07:00
     out of it. It was the DEVICE's "now minus seven hours" — so a tablet set to
     another zone asked for the wrong desk day. There is no date CONTROL, by
     design: the screen shows the desk day that is open, and the key only moves
     when the building does. */
  const [deskDay, setDeskDay] = useState(() => deskDayOf(buildingZone));

  // A different building may be on a different desk day at this instant.
  useEffect(() => {
    setDeskDay(deskDayOf(buildingZone));
  }, [buildingZone, selectedBuilding?.id]);

  /* The window in words, for the note under the toggle. `deskDay` is a day KEY
     — the building's calendar date the desk day opens on — so it is printed as
     a date and never through a Date on this device's clock. */
  const deskDayLabel = (() => {
    const day = (key: string) =>
      formatDayKey(key, { weekday: 'short', day: 'numeric', month: 'short' });

    return `Desk day ${day(deskDay)} 07:00 → ${day(addDays(deskDay, 1))} 07:00`;
  })();


  // FIRST READ vs RE-READ. The skeleton is for the first read only (nothing to
  // show yet); a building change keeps the table on screen and dims it
  // (Staleable) — never swap a table for a skeleton on a refetch.
  const reportState =
    loading && logs === undefined ? 'loading' : error ? 'error' : 'ready';

  useEffect(() => {
    let active = true;

    const fetchLogs = async () => {
      try {
        // The desk day is 07:00 to 07:00 on the BUILDING's clock and the server
        // cuts it there for every caller. No zone is sent with the request.
        if (selectedBuilding) {
          setLoading(true);
          setError('');
          const response = await fetchAllItems(
            `logs?buildingId=${selectedBuilding.id}&date=${deskDay}`
          );
          if (active) {
            setLogs(response);
          }
        }
      } catch (error) {
        console.error('Failed to fetch logs:', error);
        if (active) {
          setError('We could not load this report. Please try again.');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    // Trigger fetchLogs only if selectedBuilding and deskDay are present
    if (selectedBuilding && deskDay) {
      fetchLogs();
    }

    return () => {
      active = false;
    };
  }, [selectedBuilding, deskDay]); // Dependencies are selectedBuilding and deskDay

  return (
    <NavbarAndContentContainer>
      <ContentAndButtonsContainer dashboard>
        {/* TOP LEFT, one design, one component — see
            components/layout/back-button.tsx. It replaced a bottom-left
            <NavButton direction="back">, which put the way OFF this screen
            in the same corner and the same shape as the wizard's
            step-back. */}
        {/* $gutter="md" matches ButtonsWrapper and ContentContainer below,
            which both pad `spacing.md`. This container is the dashboard
            variant and pads nothing. */}
        <BackBar $gutter="md">
          <BackButton to="/main-menu" label="Back to menu" />
        </BackBar>
        {/* THE PAGE'S ONE H1, and it was missing entirely — measured:
            zero heading elements of any level on this route. A top-level
            view whose title is not a heading gives a screen-reader user
            nothing to jump to and starts the document outline at level 2.

            VISUALLY HIDDEN rather than drawn: the title this page needs is
            the one `useDocumentTitle` already sets, the screen's visual
            design puts its identity in the back bar and the content, and
            adding a drawn heading here would be a design change smuggled
            into an accessibility fix. */}
        <VisuallyHidden as="h1">Daily report</VisuallyHidden>
        {/* TWO CHOICES, THE CURRENT ONE MARKED — not one button labelled
            with the view it is NOT showing.
            It was a single control, permanently `isSelected`, reading "See
            Visitor Logs" while visitor logs were on screen. Nothing on the
            page named the view being displayed, so the label was the only
            clue and it pointed the wrong way. `aria-pressed` was absent too,
            so a screen reader was told nothing about state at all. */}
        <ButtonsWrapper role="group" aria-label="Report view">
          <ReportToggleButton
            type="button"
            isSelected={isVisitorLog}
            aria-pressed={isVisitorLog}
            onClick={() => setIsVisitorLog(true)}
          >
            <Text
              as="span"
              width={'100'}
              fontFamily={font.family}
              fontSize={font.size.xs}
              fontWeight={600}
              mb={0}
              mt={0}
            >
              Visitor logs
            </Text>
          </ReportToggleButton>
          <ReportToggleButton
            type="button"
            isSelected={!isVisitorLog}
            aria-pressed={!isVisitorLog}
            onClick={() => setIsVisitorLog(false)}
          >
            <Text
              as="span"
              width={'100'}
              fontFamily={font.family}
              fontSize={font.size.xs}
              fontWeight={600}
              mb={0}
              mt={0}
            >
              Building Activities
            </Text>
          </ReportToggleButton>
        </ButtonsWrapper>
        {/* WHICH DAY IS THIS?
            The report shows the DESK day, which runs 07:00 to 07:00 in the
            building's own zone (visitor-log.js:520) — not midnight to
            midnight — and this screen has no date control by design. So a
            concierge who logs a visitor at 06:50 and opens the report a
            minute later is looking at a DIFFERENT day's sheet, sees "No
            entries logged for this report yet", and has nothing on screen to
            explain it. Measured exactly that way during the runtime audit:
            five entries written at 06:49-06:51 UTC, all correctly filed
            under the previous desk day, and a report that simply looked
            empty.
            Naming the window costs one line and answers the question. */}
        <DeskDayNote>{deskDayLabel}</DeskDayNote>
        <ClockNote clock={clock} />
        <ContentContainer>
          {/* Skeleton, error and table are fills of ONE box that crossfade;
              the floor is the skeleton's own height, so the card does not
              shrink and grow between them. */}
          <ReportSwitch state={reportState} minHeight={240}>
            {reportState === 'loading' ? (
              <ReportSkeleton />
            ) : reportState === 'error' ? (
              <EmptyState role="alert">{error}</EmptyState>
            ) : (
              <Staleable stale={loading}>
                <ReportsTable
                  log={isVisitorLog ? logs?.visitorLogs : logs?.dailyLogs}
                  tz={clock.zone}
                />
              </Staleable>
            )}
          </ReportSwitch>
        </ContentContainer>
      </ContentAndButtonsContainer>
    </NavbarAndContentContainer>
  );
};

